import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarClock, Landmark, Plus, FileSearch, HelpCircle } from "lucide-react";
import { ActiveMattersTable } from "@/components/lawyer/ActiveMattersTable";
import { DealIntakesCard } from "@/components/lawyer/DealIntakesCard";
import { LawyerRequestsCard } from "@/components/lawyer/LawyerRequestsCard";
import { PendingActionsCard } from "@/components/lawyer/PendingActionsCard";
import { UpcomingMilestonesCard } from "@/components/lawyer/UpcomingMilestonesCard";
import { CreateMatterDialog } from "@/components/lawyer/CreateMatterDialog";
import { DashboardOverviewHeader } from "@/components/dashboard/DashboardOverviewHeader";
import { Link, useNavigate } from "react-router-dom";
import { PageMeta } from "@/components/seo/PageMeta";
import { useState } from "react";
import { ActivePropertyTransactionsCard } from "@/components/dashboard/ActivePropertyTransactionsCard";
import { useEscrowPayments, useMatters, formatNaira, usePendingDeals } from "@/hooks/use-supabase-queries";
import { differenceInHours, format } from "date-fns";
import { toast } from "sonner";

export default function LawyerDashboard() {
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const [isCreateMatterOpen, setIsCreateMatterOpen] = useState(false);
  const { data: matters } = useMatters();
  const { data: payments } = useEscrowPayments();
  const { data: pendingDeals } = usePendingDeals();

  const handleViewMatter = (matterId: string) => {
    navigate(`/matters/${matterId}`);
  };

  const handleReviewIntake = (intakeId: string) => {
    navigate(`/deal-intake/${intakeId}`);
  };

  const handleQuickReview = () => {
    const nextPendingDeal = pendingDeals?.[0];
    if (nextPendingDeal?.id) {
      handleReviewIntake(nextPendingDeal.id);
      return;
    }

    toast.info("No pending deal intake yet", {
      description: "New instructions will appear here as soon as an agent assigns one to you.",
    });
  };

  const urgentActions = (matters || []).filter((matter) =>
    matter.status === "active" && differenceInHours(new Date(), new Date(matter.updated_at)) >= 48
  ).length;
  const nextMilestone = (matters || [])
    .filter((matter) => matter.estimated_completion && matter.status === "active")
    .sort((left, right) => new Date(left.estimated_completion!).getTime() - new Date(right.estimated_completion!).getTime())[0];
  const professionalFeesSecured = (payments || [])
    .filter((payment) => ["funded", "awaiting_confirmations", "awaiting_admin_release"].includes(payment.status))
    .flatMap((payment) => payment.escrow_allocations || [])
    .filter((allocation: any) => allocation.recipient_user_id && allocation.recipient_role === "lawyer")
    .reduce((sum: number, allocation: any) => sum + Number(allocation.amount || 0), 0);

  return (
    <div className="p-6 space-y-6">
      <PageMeta
        title="Lawyer Dashboard"
        description="Manage active matters, review deal intakes, and track milestones in PropTRUST Navigator."
        url={window.location.href}
      />
      <DashboardOverviewHeader
        title="Lawyer Dashboard"
        subtitle="Manage active matters, review legal requests, and keep each property transaction moving through the right stage with full visibility."
        actions={(
          <>
            <Button variant="outline" onClick={handleQuickReview}>
              <FileSearch className="mr-2 h-4 w-4" />
              Review Deal Intake
            </Button>
            <Button variant="outline" asChild>
              <Link to="/help">
                <HelpCircle className="mr-2 h-4 w-4" />
                Helpline
              </Link>
            </Button>
            <Button onClick={() => setIsCreateMatterOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Matter
            </Button>
          </>
        )}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <div className={`rounded-2xl border p-4 ${urgentActions > 0 ? "border-destructive/30 bg-destructive/5" : "border-border bg-card"}`}>
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <AlertTriangle className={urgentActions > 0 ? "h-4 w-4 text-destructive" : "h-4 w-4 text-muted-foreground"} />
            Urgent Actions
          </div>
          <p className="mt-3 text-2xl font-bold text-foreground">{urgentActions}</p>
          <p className="mt-1 text-sm text-muted-foreground">Stages or matters quiet for over 48 hours</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <CalendarClock className="h-4 w-4 text-primary" />
            Next Milestone
          </div>
          <p className="mt-3 text-lg font-semibold text-foreground">
            {nextMilestone?.estimated_completion ? format(new Date(nextMilestone.estimated_completion), "MMM d, yyyy") : "No deadline set"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{nextMilestone?.title || "Set target dates for title search, consent, or closing."}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Landmark className="h-4 w-4 text-primary" />
            Professional Fees Secured
          </div>
          <p className="mt-3 text-2xl font-bold text-foreground">{formatNaira(professionalFeesSecured)}</p>
          <p className="mt-1 text-sm text-muted-foreground">Legal fees protected in active escrow plans</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Active Matters (spans 2 columns on large screens) */}
        <div className="lg:col-span-2 space-y-6">
          <DealIntakesCard onReviewIntake={handleReviewIntake} />
          <LawyerRequestsCard />
          <ActiveMattersTable onViewMatter={handleViewMatter} />
        </div>

        {/* Right Column - Actions & Milestones */}
        <div className="space-y-6">
          <ActivePropertyTransactionsCard
            title="Accepted Property Transactions"
            emptyText="Accepted under-offer property requests will show up here once they move into active coordination."
          />
          <PendingActionsCard onOpenMatter={handleViewMatter} />
          <UpcomingMilestonesCard onOpenMatter={handleViewMatter} />
        </div>
      </div>

      <CreateMatterDialog
        open={isCreateMatterOpen}
        onOpenChange={setIsCreateMatterOpen}
        onCreatedMatter={(matterId) => navigate(`/matters/${matterId}`)}
      />
    </div>
  );
}
